'use client';

import { useEffect, useRef, useCallback } from 'react';

export type CrmSyncScope =
  | 'all'
  | 'wallet'
  | 'balances'
  | 'profile'
  | 'accounts'
  | 'deposits'
  | 'withdrawals'
  | 'transactions'
  | 'positions'
  | 'kyc'
  | 'tickets'
  | 'streaks'
  | 'transfers';

const CRM_INVALIDATE_EVENT = 'crm:data-invalidate';

/**
 * Triggers an event to invalidate and refetch data across all active hooks and screens matching scope.
 */
export function invalidateCrmData(scope: CrmSyncScope | CrmSyncScope[]): void {
  if (typeof window === 'undefined') return;

  const scopes = Array.isArray(scope) ? scope : [scope];
  const event = new CustomEvent(CRM_INVALIDATE_EVENT, {
    detail: { scopes, timestamp: Date.now() },
  });
  window.dispatchEvent(event);
}

export interface UseLiveSyncOptions {
  /**
   * The fetch or refresh callback to execute.
   */
  onSync: () => Promise<any> | any;
  /**
   * Sync scope identifier(s) to listen for manual or mutation invalidations.
   */
  scope?: CrmSyncScope | CrmSyncScope[];
  /**
   * Periodic polling interval in milliseconds while the page is visible.
   * Set to 0 or null to disable polling and only rely on lifecycle/invalidation events.
   * Default: 15000 (15 seconds).
   */
  intervalMs?: number | null;
  /**
   * Whether live sync is enabled. Default: true.
   */
  enabled?: boolean;
  /**
   * Minimum time in ms between consecutive sync executions (anti-hammering debounce).
   * Default: 2000ms.
   */
  minIntervalBetweenSyncsMs?: number;
}

/**
 * Enterprise live sync hook supporting iOS Standalone PWA, Safari, and Desktop browsers.
 * Handles:
 * - App return from background (visibilitychange === 'visible')
 * - iOS page restore (pageshow & bfcache)
 * - Window focus
 * - Network reconnection (online)
 * - Scope-based invalidation events across components
 * - Background-paused periodic polling with in-flight overlap protection
 */
export function useLiveSync({
  onSync,
  scope,
  intervalMs = 15000,
  enabled = true,
  minIntervalBetweenSyncsMs = 2000,
}: UseLiveSyncOptions): void {
  const onSyncRef = useRef(onSync);
  onSyncRef.current = onSync;

  const isSyncingRef = useRef(false);
  const lastSyncTimeRef = useRef(0);
  const intervalTimerRef = useRef<NodeJS.Timeout | null>(null);

  const scopesRef = useRef<string[]>(
    scope ? (Array.isArray(scope) ? scope : [scope]) : ['all']
  );
  scopesRef.current = scope ? (Array.isArray(scope) ? scope : [scope]) : ['all'];

  const executeSync = useCallback(async (forced = false) => {
    if (!enabled) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden' && !forced) {
      return; // Do not execute background polling when tab/app is hidden
    }

    const now = Date.now();
    if (!forced && now - lastSyncTimeRef.current < minIntervalBetweenSyncsMs) {
      return; // Skip if executed too recently
    }

    if (isSyncingRef.current) {
      return; // Overlapping request protection
    }

    isSyncingRef.current = true;
    try {
      await onSyncRef.current();
      lastSyncTimeRef.current = Date.now();
    } catch (err) {
      console.warn('[LiveSync] Error executing sync:', err);
    } finally {
      isSyncingRef.current = false;
    }
  }, [enabled, minIntervalBetweenSyncsMs]);

  // Manage periodic polling timer
  useEffect(() => {
    if (!enabled || !intervalMs || intervalMs <= 0 || typeof window === 'undefined') {
      return;
    }

    const startTimer = () => {
      stopTimer();
      if (document.visibilityState === 'visible') {
        intervalTimerRef.current = setInterval(() => {
          executeSync(false);
        }, intervalMs);
      }
    };

    const stopTimer = () => {
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
        intervalTimerRef.current = null;
      }
    };

    startTimer();

    return () => {
      stopTimer();
    };
  }, [enabled, intervalMs, executeSync]);

  // Manage visibility, pageshow, focus, online, and custom invalidation listeners
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    // 1. iOS PWA / Safari visibilitychange
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // App resumed or tab selected: execute immediate catch-up sync
        executeSync(true);
      }
    };

    // 2. iOS bfcache / Home Screen app resume
    const handlePageShow = (e: PageTransitionEvent) => {
      executeSync(true);
    };

    // 3. Window focus
    const handleFocus = () => {
      executeSync(false);
    };

    // 4. Network online restoration
    const handleOnline = () => {
      executeSync(true);
    };

    // 5. Cross-component mutation invalidation
    const handleInvalidate = (e: Event) => {
      const customEvent = e as CustomEvent<{ scopes?: string[] }>;
      const incomingScopes = customEvent.detail?.scopes || ['all'];
      const myScopes = scopesRef.current;

      const shouldSync =
        incomingScopes.includes('all') ||
        myScopes.includes('all') ||
        incomingScopes.some((s) => myScopes.includes(s));

      if (shouldSync) {
        executeSync(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange, false);
    window.addEventListener('pageshow', handlePageShow, false);
    window.addEventListener('focus', handleFocus, false);
    window.addEventListener('online', handleOnline, false);
    window.addEventListener(CRM_INVALIDATE_EVENT, handleInvalidate, false);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener(CRM_INVALIDATE_EVENT, handleInvalidate);
    };
  }, [enabled, executeSync]);
}
